'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@radix-ui/react-icons';
import { DayPicker } from 'react-day-picker';
import { cn } from '@/utils/shad';
import { Button, buttonVariants } from '@/comps/primitives/button';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/comps/primitives/tooltip';
export type CalendarProps = React.ComponentProps<typeof DayPicker> & {
  alignTimeRangeToToday?: (selectedDate: {
    from: Date | undefined;
    to?: Date;
  } | undefined) => void;
};
function Calendar(t0) {
  const $ = _c(28);
  let alignTimeRangeToToday;
  let className;
  let classNames;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      classNames,
      showOutsideDays: t1,
      alignTimeRangeToToday,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = alignTimeRangeToToday;
    $[2] = className;
    $[3] = classNames;
    $[4] = props;
    $[5] = t1;
  } else {
    alignTimeRangeToToday = $[1];
    className = $[2];
    classNames = $[3];
    props = $[4];
    t1 = $[5];
  }
  const showOutsideDays = t1 === undefined ? true : t1;
  let t2;
  if ($[6] !== alignTimeRangeToToday || $[7] !== props.mode || $[8] !== props.selected) {
    t2 = props.mode === "range" && props.selected?.from && props.selected?.from < new Date() && !props.selected.to && <div className="w-full px-2"><Button className="w-full" variant="outline" onClick={() => {
        alignTimeRangeToToday?.({
          from: props.selected?.from,
          to: new Date()
        });
      }}>Keep end date synced with current date<Tooltip><TooltipTrigger asChild={true}><span className="inline-flex" onClick={_temp}><Info size={12} /></span></TooltipTrigger><TooltipContent>By clicking this button the time range will be kept in sync with the current date</TooltipContent></Tooltip></Button></div>;
    $[6] = alignTimeRangeToToday;
    $[7] = props.mode;
    $[8] = props.selected;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== className) {
    t3 = cn("p-3", className);
    $[10] = className;
    $[11] = t3;
  } else {
    t3 = $[11];
  }
  let t4;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = cn(buttonVariants({
      variant: "outline"
    }), "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100");
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const t5 = props.mode === "range" ? "[&:has(>.day-range-end)]:rounded-r-md [&:has(>.day-range-start)]:rounded-l-md" : "has-aria-[selected]:rounded-md";
  let t6;
  if ($[13] !== t5) {
    t6 = cn("relative p-0 text-center text-sm focus-within:z-20 has-aria-[selected]:bg-accent [&:has([aria-selected].day-range-end)]:rounded-r-md", t5);
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  let t7;
  if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = cn(buttonVariants({
      variant: "ghost"
    }), "h-8 w-8 p-0 font-normal aria-selected:opacity-100");
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  let t8;
  if ($[16] !== classNames || $[17] !== t6) {
    t8 = {
      months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
      month: "space-y-4",
      caption: "flex justify-center pt-1 relative items-center",
      caption_label: "text-sm font-medium",
      nav: "space-x-1 flex items-center",
      nav_button: t4,
      nav_button_previous: "absolute left-1",
      nav_button_next: "absolute right-1",
      table: "w-full border-collapse space-y-1",
      head_row: "flex",
      head_cell: "text-muted-foreground rounded-md w-8 font-normal text-[0.8rem]",
      row: "flex w-full mt-2",
      cell: t6,
      day: t7,
      day_range_start: "day-range-start",
      day_range_end: "day-range-end",
      day_selected: "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground",
      day_today: "bg-accent text-accent-foreground",
      day_outside: "day-outside text-muted-foreground opacity-50  aria-selected:bg-accent/50",
      day_disabled: "text-muted-foreground opacity-50",
      day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
      day_hidden: "invisible",
      ...classNames
    };
    $[16] = classNames;
    $[17] = t6;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = {
      IconLeft: _temp2,
      IconRight: _temp3
    };
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  let t10;
  if ($[20] !== props || $[21] !== showOutsideDays || $[22] !== t3 || $[23] !== t8) {
    t10 = <DayPicker showOutsideDays={showOutsideDays} className={t3} classNames={t8} components={t9} {...props} />;
    $[20] = props;
    $[21] = showOutsideDays;
    $[22] = t3;
    $[23] = t8;
    $[24] = t10;
  } else {
    t10 = $[24];
  }
  let t11;
  if ($[25] !== t10 || $[26] !== t2) {
    t11 = <div className="flex flex-col items-center">{t2}{t10}</div>;
    $[25] = t10;
    $[26] = t2;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
}
function _temp3(t0) {
  return <ChevronRightIcon className="h-4 w-4" />;
}
function _temp2(t0) {
  return <ChevronLeftIcon className="h-4 w-4" />;
}
function _temp(event) {
  return event.stopPropagation();
}
Calendar.displayName = 'Calendar';
export { Calendar };